import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Mail, Phone, MapPin, Globe, Save, Loader2, Pencil, X, Compass } from 'lucide-react';
import { usePartnerAuth } from '@/hooks/usePartnerAuth';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  website: string;
  deliveryRadius: number;
}

const PartnerSettings = () => {
  const { partner, hasPermission, isLoading: partnerAuthLoading } = usePartnerAuth();
  const { isReadOnly } = useReadOnly();
  const [isLoading, setIsLoading] = useState(true);

  const canManageSettings = hasPermission('manage_settings');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingServiceArea, setIsSavingServiceArea] = useState(false);
  const [isEditingServiceArea, setIsEditingServiceArea] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    website: '',
    deliveryRadius: 25
  });
  const [originalData, setOriginalData] = useState<FormData | null>(null);
  const [originalRadius, setOriginalRadius] = useState<number>(25);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  useEffect(() => {
    if (partner) {
      const data: FormData = {
        name: partner.name || '',
        email: partner.email || '',
        phone: partner.phone || '',
        address: partner.street_address || '',
        city: partner.city || '',
        state: partner.state || '',
        zip: partner.zip || '',
        website: partner.website || '',
        deliveryRadius: partner.delivery_radius_miles || 25
      };
      setFormData(data);
      setOriginalData(data);
      setOriginalRadius(data.deliveryRadius);
      setIsLoading(false);
    }
  }, [partner]);

  const hasChanges = () => {
    if (!originalData) return false;
    return (
      formData.name !== originalData.name ||
      formData.email !== originalData.email ||
      formData.phone !== originalData.phone ||
      formData.address !== originalData.address ||
      formData.city !== originalData.city ||
      formData.state !== originalData.state ||
      formData.zip !== originalData.zip ||
      formData.website !== originalData.website
    );
  };

  const hasServiceAreaChanges = () => {
    return formData.deliveryRadius !== originalRadius;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Business name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Business name must be less than 100 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^[\d\s\-+()]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData.address && formData.address.length > 200) {
      newErrors.address = 'Address must be less than 200 characters';
    }

    if (formData.website && formData.website.trim()) {
      if (!/^https?:\/\/.+/.test(formData.website)) {
        newErrors.website = 'Website must start with http:// or https://';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !partner?.id) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('fulfillment_partners')
        .update({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
          website: formData.website.trim() || null,
        })
        .eq('id', partner.id);

      if (error) throw error;

      setOriginalData({ ...formData });
      setIsEditing(false);
      toast.success('Settings saved successfully');
    } catch (error: unknown) {
      console.error('Error saving settings:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveServiceArea = async () => {
    if (!partner?.id) return;

    setIsSavingServiceArea(true);
    try {
      const { error } = await supabase
        .from('fulfillment_partners')
        .update({
          delivery_radius_miles: formData.deliveryRadius
        })
        .eq('id', partner.id);

      if (error) throw error;

      setOriginalRadius(formData.deliveryRadius);
      setIsEditingServiceArea(false);
      toast.success('Service area saved successfully');
    } catch (error: unknown) {
      console.error('Error saving service area:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save service area');
    } finally {
      setIsSavingServiceArea(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setFormData({ ...originalData, deliveryRadius: formData.deliveryRadius });
    }
    setErrors({});
    setIsEditing(false);
  };

  const handleCancelServiceArea = () => {
    setFormData((prev) => ({ ...prev, deliveryRadius: originalRadius }));
    setIsEditingServiceArea(false);
  };

  if (partnerAuthLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!canManageSettings) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your business profile and preferences
        </p>
      </div>

      {/* Business Information Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Business Information
            </CardTitle>
            <CardDescription>
              {isEditing ? 'Update your business details' : 'Your business details visible to your team'}
            </CardDescription>
          </div>
          {!isEditing && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isEditing ? (
            // Edit Mode
            <div className="space-y-4">
              {/* Business Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Business Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Your Business Name"
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              {/* Contact Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Contact Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="contact@business.com"
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className={errors.phone ? 'border-destructive' : ''}
                />
                {errors.phone && (
                  <p className="text-sm text-destructive">{errors.phone}</p>
                )}
              </div>

              {/* Address (locked) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Address
                </Label>
                <p className="text-sm py-2 px-3 rounded-md border bg-muted/50 text-muted-foreground">
                  {[formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(', ') || 'Not provided'}
                </p>
                <p className="text-xs text-muted-foreground">
                  To update your address, please contact us at support@pontis.life
                </p>
              </div>

              {/* Website */}
              <div className="space-y-2">
                <Label htmlFor="website" className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  Website
                </Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://www.example.com"
                  className={errors.website ? 'border-destructive' : ''}
                />
                {errors.website && (
                  <p className="text-sm text-destructive">{errors.website}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges() || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="space-y-6">
              {/* Business Name */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  Business Name
                </div>
                <p className="text-base">{formData.name || 'Not provided'}</p>
              </div>

              {/* Contact Email */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  Contact Email
                </div>
                <p className="text-base">{formData.email || 'Not provided'}</p>
              </div>

              {/* Phone Number */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </div>
                <p className="text-base">{formData.phone || 'Not provided'}</p>
              </div>

              {/* Address */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  Address
                </div>
                <p className="text-base">
                  {[formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(', ') || 'Not provided'}
                </p>
              </div>

              {/* Website */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  Website
                </div>
                {formData.website ? (
                  <a
                    href={formData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-primary hover:underline"
                  >
                    {formData.website}
                  </a>
                ) : (
                  <p className="text-base">Not provided</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service Area Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Compass className="h-5 w-5" />
              Service Area
            </CardTitle>
            <CardDescription>
              {isEditingServiceArea ? 'Update your delivery service area' : 'Your delivery service area'}
            </CardDescription>
          </div>
          {!isEditingServiceArea && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={() => setIsEditingServiceArea(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isEditingServiceArea ? (
            // Edit Mode - Service Area
            <div className="space-y-4">
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="deliveryRadius">Delivery Radius (miles)</Label>
                <Input
                  id="deliveryRadius"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.deliveryRadius}
                  onChange={(e) => handleChange('deliveryRadius', parseInt(e.target.value) || 25)}
                />
                <p className="text-xs text-muted-foreground">
                  You'll receive orders for gravesites within this radius of your location
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleCancelServiceArea}
                  disabled={isSavingServiceArea}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveServiceArea}
                  disabled={!hasServiceAreaChanges() || isSavingServiceArea}
                >
                  {isSavingServiceArea ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // View Mode - Service Area
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Compass className="h-4 w-4" />
                Delivery Radius
              </div>
              <p className="text-base">{formData.deliveryRadius} miles</p>
              <p className="text-xs text-muted-foreground">
                You'll receive orders for gravesites within this radius of your location
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerSettings;
